import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import MetricCard from '../admin-dashboard/components/MetricCard';
import CompletionOverview from './components/CompletionOverview';
import MissingFilesSection from './components/MissingFilesSection';
import VoiceActorStatus from './components/VoiceActorStatus';
import CompletionChart from './components/CompletionChart';
import { getAudioStatistics, getMissingAudioLines } from '../../services/audioManagementService';
import { voiceActorService } from '../../services/voiceActorService';
import { batchSendProductionUpdates, checkAndSendDeadlineAlerts } from '../../services/emailNotificationService';
import { realtimeService } from '../../services/realtimeSubscriptionService';
import { supabase } from '../../lib/supabase';

export default function AudioProductionDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [audioStats, setAudioStats] = useState(null);
  const [missingLines, setMissingLines] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [voiceActors, setVoiceActors] = useState([]);
  const [selectedPlay, setSelectedPlay] = useState('all');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingDeadlineAlerts, setSendingDeadlineAlerts] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailError, setEmailError] = useState('');
  const [realtimeStatus, setRealtimeStatus] = useState('disconnected');

  useEffect(() => {
    loadDashboardData();
    setupRealtimeSubscriptions();

    return () => {
      // Cleanup subscriptions on unmount
      realtimeService?.unsubscribeAll();
    };
  }, []);

  const setupRealtimeSubscriptions = () => {
    setRealtimeStatus('connecting');

    // Subscribe to recording sessions for live status updates
    realtimeService?.subscribeToRecordingSessions((payload) => {
      console.log('Recording session update:', payload);
      // Reload data when sessions change
      loadDashboardData();
    });

    // Subscribe to voice actor assignments
    realtimeService?.subscribeToAssignments((payload) => {
      console.log('Assignment update:', payload);
      // Reload assignments when they change
      loadAssignments();
    });

    // Subscribe to voice actor availability
    realtimeService?.subscribeToVoiceActorAvailability((payload) => {
      console.log('Voice actor availability update:', payload);
      // Reload voice actors when availability changes
      loadVoiceActors();
    });

    // Subscribe to audio recordings for progress tracking
    realtimeService?.subscribeToAudioRecordings(null, (payload) => {
      console.log('Audio recording update:', payload);
      // Reload audio stats when recordings change
      loadAudioStats();
    });

    // Subscribe to broadcast channels
    realtimeService?.subscribeToSessionStatusBroadcast((payload) => {
      console.log('Session status broadcast:', payload);
      showRealtimeNotification(`Session ${payload?.payload?.status}`, 'info');
    });

    realtimeService?.subscribeToRecordingProgressBroadcast((payload) => {
      console.log('Recording progress broadcast:', payload);
      showRealtimeNotification(
        `Recording progress: ${payload?.payload?.progress}% complete`,
        'success'
      );
      // Reload audio stats to reflect progress
      loadAudioStats();
    });

    realtimeService?.subscribeToAvailabilityBroadcast((payload) => {
      console.log('Availability broadcast:', payload);
      const status = payload?.payload?.isAvailable ? 'available' : 'unavailable';
      showRealtimeNotification(`Voice actor is now ${status}`, 'info');
      // Reload voice actors
      loadVoiceActors();
    });

    setRealtimeStatus('connected');
  };

  const showRealtimeNotification = (message, type = 'info') => {
    // Create a temporary notification
    const notification = document.createElement('div');
    notification.className = `fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg transition-all transform translate-x-0 ${
      type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-blue-500'
    } text-white`;
    notification.textContent = message;
    document.body?.appendChild(notification);

    // Remove after 3 seconds
    setTimeout(() => {
      notification?.classList?.add('translate-x-full', 'opacity-0');
      setTimeout(() => notification?.remove(), 300);
    }, 3000);
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [stats, missing, actorAssignments, actors] = await Promise.all([
        getAudioStatistics(),
        getMissingAudioLines(),
        voiceActorService?.getAssignments(),
        voiceActorService?.getAllVoiceActors()
      ]);

      setAudioStats(stats);
      setMissingLines(missing);
      setAssignments(actorAssignments);
      setVoiceActors(actors);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAudioStats = async () => {
    try {
      const stats = await getAudioStatistics();
      setAudioStats(stats);
    } catch (error) {
      console.error('Error loading audio stats:', error);
    }
  };

  const loadAssignments = async () => {
    try {
      const actorAssignments = await voiceActorService?.getAssignments();
      setAssignments(actorAssignments);
    } catch (error) {
      console.error('Error loading assignments:', error);
    }
  };

  const loadVoiceActors = async () => {
    try {
      const actors = await voiceActorService?.getAllVoiceActors();
      setVoiceActors(actors);
    } catch (error) {
      console.error('Error loading voice actors:', error);
    }
  };

  const getPlayCompletionData = () => {
    if (!audioStats?.playBreakdown) return [];

    return Object.entries(audioStats?.playBreakdown)?.map(([playTitle, missingCount]) => {
      const totalForPlay = missingLines?.filter(line => line?.plays?.title === playTitle)?.length;
      const completed = totalForPlay - missingCount;
      const completionRate = totalForPlay > 0 ? Math.round((completed / totalForPlay) * 100) : 0;

      return {
        playTitle,
        missingCount,
        completionRate,
        totalLines: totalForPlay,
        status: completionRate >= 80 ? 'on-track' : completionRate >= 50 ? 'at-risk' : 'critical'
      };
    });
  };

  const getVoiceActorWorkload = () => {
    return voiceActors?.map(actor => {
      const actorAssignments = assignments?.filter(a => a?.voiceActorId === actor?.id);
      const totalLines = actorAssignments?.reduce((sum, a) => sum + (a?.lineGroup?.lineCount || 0), 0);
      const completedAssignments = actorAssignments?.filter(a => a?.status === 'completed')?.length;
      const pendingAssignments = actorAssignments?.filter(a => a?.status === 'pending')?.length;
      const inProgressAssignments = actorAssignments?.filter(a => a?.status === 'in_progress')?.length;

      return {
        ...actor,
        totalAssignments: actorAssignments?.length,
        totalLines,
        completedAssignments,
        pendingAssignments,
        inProgressAssignments,
        utilizationRate: actorAssignments?.length > 0 ? Math.round((completedAssignments / actorAssignments?.length) * 100) : 0
      };
    });
  };

  const handleSendProductionUpdate = async () => {
    try {
      setSendingEmail(true);
      setEmailError('');
      setEmailSuccess('');

      // Get all admin users
      const { data: adminUsers, error } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name')
        ?.in('role', ['admin', 'superadmin']);

      if (error) throw error;

      if (!adminUsers || adminUsers?.length === 0) {
        throw new Error('No admin users found to send notifications');
      }

      // Send batch production updates
      const result = await batchSendProductionUpdates(audioStats, adminUsers);

      setEmailSuccess(`Production update sent to ${result?.sent} admin(s) successfully!`);
      
      // Clear success message after 5 seconds
      setTimeout(() => setEmailSuccess(''), 5000);
    } catch (error) {
      console.error('Error sending production update:', error);
      setEmailError(error?.message || 'Failed to send production update');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendDeadlineAlerts = async () => {
    try {
      setSendingDeadlineAlerts(true);
      setEmailError('');
      setEmailSuccess('');

      const result = await checkAndSendDeadlineAlerts(3); // Check 3 days ahead

      if (result?.sent === 0) {
        setEmailSuccess('No upcoming deadlines found within the next 3 days.');
      } else {
        setEmailSuccess(`Deadline alerts sent to ${result?.sent} voice actor(s) successfully!`);
      }
      
      // Clear success message after 5 seconds
      setTimeout(() => setEmailSuccess(''), 5000);
    } catch (error) {
      console.error('Error sending deadline alerts:', error);
      setEmailError(error?.message || 'Failed to send deadline alerts');
    } finally {
      setSendingDeadlineAlerts(false);
    }
  };

  const metrics = [
    {
      title: 'Overall Completion',
      value: `${audioStats?.completionPercentage || 0}%`,
      change: audioStats?.completionPercentage >= 75 ? 'On Track' : 'Needs Attention',
      changeType: audioStats?.completionPercentage >= 75 ? 'positive' : 'negative',
      trend: audioStats?.completionPercentage >= 75 ? 'up' : 'down',
      icon: 'Music',
      iconColor: 'bg-primary'
    },
    {
      title: 'Lines Recorded',
      value: audioStats?.completedAudio?.toLocaleString() || '0',
      change: `${audioStats?.totalLines || 0} total`,
      changeType: 'positive',
      trend: 'up',
      icon: 'Mic',
      iconColor: 'bg-accent'
    },
    {
      title: 'Missing Audio Files',
      value: audioStats?.missingAudio?.toLocaleString() || '0',
      change: `${Object.keys(audioStats?.playBreakdown || {})?.length} plays affected`,
      changeType: audioStats?.missingAudio > 100 ? 'negative' : 'neutral',
      trend: audioStats?.missingAudio > 100 ? 'down' : 'up',
      icon: 'AlertTriangle',
      iconColor: 'bg-warning'
    },
    {
      title: 'Active Voice Actors',
      value: voiceActors?.filter(a => a?.isAvailable)?.length?.toString(),
      change: `${assignments?.filter(a => a?.status === 'in_progress')?.length} in progress`,
      changeType: 'positive',
      trend: 'up',
      icon: 'Users',
      iconColor: 'bg-success'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading dashboard data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation />
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <BreadcrumbTrail
          items={[
            { label: 'Admin Dashboard', path: '/admin-dashboard' },
            { label: 'Audio Production Dashboard', path: '/audio-production-dashboard' }
          ]}
        />

        <div className="mb-8">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-4xl font-bold text-foreground mb-2">Audio Production Dashboard</h1>
                {/* Real-time Status Indicator */}
                <div className="flex items-center gap-2 px-3 py-1 rounded-full text-sm" 
                     style={{ backgroundColor: realtimeStatus === 'connected' ? '#10b981' : '#6b7280', color: 'white' }}>
                  <div className={`w-2 h-2 rounded-full ${
                    realtimeStatus === 'connected' ? 'bg-white animate-pulse' : 'bg-gray-300'
                  }`}></div>
                  <span>{realtimeStatus === 'connected' ? 'Live' : 'Connecting...'}</span>
                </div>
              </div>
              <p className="text-muted-foreground">Monitor recording progress, missing files, and voice actor assignments in real-time</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSendDeadlineAlerts}
                disabled={sendingDeadlineAlerts}
                className="flex items-center gap-2 px-4 py-2 bg-warning text-white rounded-lg hover:bg-warning/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingDeadlineAlerts ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Send Deadline Alerts</span>
                  </>
                )}
              </button>
              <button
                onClick={handleSendProductionUpdate}
                disabled={sendingEmail}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingEmail ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>Send Status Update</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Email Notifications */}
          {emailSuccess && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <svg className="w-5 h-5 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-green-800 flex-1">{emailSuccess}</p>
              <button onClick={() => setEmailSuccess('')} className="text-green-600 hover:text-green-800">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}

          {emailError && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-red-800 flex-1">{emailError}</p>
              <button onClick={() => setEmailError('')} className="text-red-600 hover:text-red-800">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Metrics Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metrics?.map((metric, index) => (
            <MetricCard key={index} {...metric} />
          ))}
        </div>

        {/* Completion Overview */}
        <CompletionOverview 
          playData={getPlayCompletionData()}
          onPlaySelect={setSelectedPlay}
          selectedPlay={selectedPlay}
        />

        {/* Completion Chart */}
        <CompletionChart playData={getPlayCompletionData()} />

        {/* Missing Files Section */}
        <MissingFilesSection 
          missingLines={selectedPlay === 'all' ? missingLines : missingLines?.filter(line => line?.plays?.title === selectedPlay)}
          playFilter={selectedPlay}
        />

        {/* Voice Actor Status */}
        <VoiceActorStatus 
          voiceActors={getVoiceActorWorkload()}
          onNavigateToAssignments={() => navigate('/voice-actor-assignment-interface')}
        />
      </div>
    </div>
  );
}